import * as translator from '../../../utils/translator';
import type { PaymentCallbacks, PrimerError } from '../paymentTypes';

// ============================================================================
// Primer Refusal Message
// ============================================================================

// Primer normalises processor declines into its own codes; group them onto a few messages.
// TODO(primer-spike): confirm the codes the sandbox actually returns for each processor.
const PRIMER_DECLINE_CODE_GROUPS: Record<string, string> = {
  INSUFFICIENT_FUNDS: 'INSUFFICIENT_FUNDS',
  WITHDRAWAL_LIMIT_EXCEEDED: 'INSUFFICIENT_FUNDS',
  AUTHENTICATION_REQUIRED: 'AUTHENTICATION_FAILED',
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
  THREE_DS_AUTH_FAILED: 'AUTHENTICATION_FAILED',
  DO_NOT_HONOR: 'DECLINED',
  DECLINED: 'DECLINED',
  REFER_TO_CARD_ISSUER: 'DECLINED',
  SUSPECTED_FRAUD: 'DECLINED',
  LOST_OR_STOLEN_CARD: 'DECLINED',
  EXPIRED_CARD: 'EXPIRED_CARD',
  INVALID_CARD_NUMBER: 'INVALID_CARD_DATA',
  INVALID_EXPIRY_DATE: 'INVALID_CARD_DATA',
  INVALID_SECURITY_CODE: 'INVALID_CARD_DATA',
  INVALID_CVV: 'INVALID_CARD_DATA',
};

export function getPrimerRefusalMessage(language: string, primerError?: PrimerError): string {
  const defaultMsg = translator.translate(language, 'payment', 'primer/refusalCode/DEFAULT');
  const code = primerError?.code?.toUpperCase();
  if (!code) return defaultMsg;

  // An exact code translation wins, then the code's group, then the default
  const group = PRIMER_DECLINE_CODE_GROUPS[code];
  return translator.translate(language, 'payment', `primer/refusalCode/${code}`)
    || (group && translator.translate(language, 'payment', `primer/refusalCode/${group}`))
    || defaultMsg;
}


// ============================================================================
// Primer Redirect Return
// ============================================================================

// Universal Checkout redirect methods (e.g. PayPal without a popup) can leave the page before
// the resume step; PaymentPageCallBack reads the payment id back from here.
export const PRIMER_PENDING_PAYMENT_STORAGE_KEY = 'primer_pending_payment';

export function savePrimerPendingPayment(hashID: string, primerPaymentId: string) {
  try {
    sessionStorage.setItem(PRIMER_PENDING_PAYMENT_STORAGE_KEY, JSON.stringify({ hashID, primerPaymentId }));
  } catch (error) {
    window.clientLogger?.warn?.('could not save primer pending payment', error);
  }
}

export function readPrimerPendingPayment(hashID: string): string | null {
  try {
    const stored = JSON.parse(sessionStorage.getItem(PRIMER_PENDING_PAYMENT_STORAGE_KEY) || 'null');
    return stored?.hashID === hashID ? stored.primerPaymentId || null : null;
  } catch (error) {
    return null;
  }
}

export function clearPrimerPendingPayment() {
  try {
    sessionStorage.removeItem(PRIMER_PENDING_PAYMENT_STORAGE_KEY);
  } catch (error) {
    // storage unavailable - nothing to clear
  }
}


// ============================================================================
// Payment Method Types
// ============================================================================

// The token only carries the instrument type (e.g. PAYPAL_ORDER); map it back to the method type
export const INSTRUMENT_TO_METHOD_TYPE: Record<string, string> = {
  PAYMENT_CARD: 'PAYMENT_CARD',
  PAYPAL_ORDER: 'PAYPAL',
  PAYPAL_BILLING_AGREEMENT: 'PAYPAL',
  APPLE_PAY: 'APPLE_PAY',
  GOOGLE_PAY: 'GOOGLE_PAY',
};

// paymentTypesRequiringIdVerification comes from the server in Adyen's vocabulary ('paypal')
const LEGACY_PAYMENT_METHOD_TYPES: Record<string, string> = {
  PAYMENT_CARD: 'scheme',
  PAYPAL: 'paypal',
  APPLE_PAY: 'applepay',
  GOOGLE_PAY: 'googlepay',
  // Primer sandbox test processors (offered by the server only in TEST)
  PRIMER_TEST_PAYPAL: 'paypal',
  PRIMER_TEST_KLARNA: 'klarna',
  PRIMER_TEST_SOFORT: 'directEbanking',
};

// Returns the entry of the verification list matching this Primer method type, if any
export function resolveVerificationType(primerType: string | undefined, typesRequiringVerification: string[] | undefined): string | undefined {
  if (!primerType || !typesRequiringVerification?.length) return undefined;
  if (typesRequiringVerification.includes(primerType)) return primerType;
  const legacyType = LEGACY_PAYMENT_METHOD_TYPES[primerType];
  return legacyType && typesRequiringVerification.includes(legacyType) ? legacyType : undefined;
}

// The ID verification disclaimer shows while PayPal needs a verification the customer hasn't done.
export const showsPaypalDisclaimer = (callbacks: PaymentCallbacks): boolean =>
  !!resolveVerificationType('PAYPAL', callbacks.paymentTypesRequiringIdVerification) && !callbacks.isIdVerified && !callbacks.hidePaypalDisclaimer;
